import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import type { PrayerCategory, PrayerStatus } from '@/pages/prayer/types';
import { formatDateTime } from '@/utils/format';

interface PrayerListRow {
  id: string;
  category: PrayerCategory;
  status: PrayerStatus;
  isAnonymous: boolean;
  assignedToMemberId: string | null;
  respondedAt: string | null;
  createdAt: string;
}

const CATEGORIES: PrayerCategory[] = [
  'health',
  'family',
  'finance',
  'spiritual',
  'thanksgiving',
  'other',
];

const STATUSES: PrayerStatus[] = ['new', 'assigned', 'praying', 'answered', 'archived'];

export const PrayerRequestsPage = () => {
  const { t } = useTranslation('prayer');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PrayerStatus | ''>('');
  const [category, setCategory] = useState<PrayerCategory | ''>('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    return params.toString();
  }, [status, category]);

  const list = useQuery({
    queryKey: ['prayer', 'requests', status, category],
    queryFn: () =>
      api.get<{ data: PrayerListRow[] }>(
        queryString ? `/prayer/requests?${queryString}` : '/prayer/requests',
      ),
  });

  const hasFilters = status !== '' || category !== '';
  const rows = list.data?.data ?? [];

  const columns: DataTableColumn<PrayerListRow>[] = [
    {
      id: 'createdAt',
      header: t('columns.date'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'category',
      header: t('columns.category'),
      cell: (row) => t(`category.${row.category}`),
    },
    {
      id: 'submitted',
      header: t('columns.submitted'),
      cell: (row) => (row.isAnonymous ? t('detail.anonymous') : t('detail.submitted')),
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => <Badge>{t(`status.${row.status}`)}</Badge>,
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="prayer.whoCanRead"
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1">
          <p className="mb-1 text-caption text-fg-secondary">{t('filters.status')}</p>
          <Listbox
            value={status}
            onChange={(value) => setStatus(value as PrayerStatus | '')}
            options={[
              { value: '', label: t('filters.allStatuses') },
              ...STATUSES.map((value) => ({ value, label: t(`status.${value}`) })),
            ]}
          />
        </div>
        <div className="min-w-[10rem] flex-1">
          <p className="mb-1 text-caption text-fg-secondary">{t('filters.category')}</p>
          <Listbox
            value={category}
            onChange={(value) => setCategory(value as PrayerCategory | '')}
            options={[
              { value: '', label: t('filters.allCategories') },
              ...CATEGORIES.map((value) => ({ value, label: t(`category.${value}`) })),
            ]}
          />
        </div>
        {hasFilters ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStatus('');
              setCategory('');
            }}
          >
            {t('filters.clear')}
          </Button>
        ) : null}
      </div>

      {list.isError ? (
        <p className="text-body-sm text-danger">{tCommon('state.errorBody')}</p>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          empty={
            <p className="text-body-sm text-fg-secondary">
              {hasFilters ? t('queue.filteredEmpty') : t('queue.emptyAll')}
            </p>
          }
          onRowClick={(row) => void navigate(`/prayer/${row.id}`)}
        />
      )}
    </div>
  );
};
